import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getVenueReviews, hideReview, unhideReview, deleteReview } from "@/api/endpoints";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Star, Eye, EyeOff, Trash2 } from "lucide-react";

function StarRow({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`size-4 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`}
        />
      ))}
    </div>
  );
}

export function ReviewsPage() {
  const { venueId } = useParams<{ venueId: string }>();
  const { t } = useI18n();
  const qc = useQueryClient();
  const [ratingFilter, setRatingFilter] = useState<string>("all");
  const [hiddenFilter, setHiddenFilter] = useState<"all" | "true" | "false">("all");
  const [page, setPage] = useState(0);
  const limit = 50;

  const { data, isLoading } = useQuery({
    queryKey: ["reviews", venueId, ratingFilter, hiddenFilter, page],
    queryFn: () =>
      getVenueReviews(venueId!, {
        rating: ratingFilter === "all" ? undefined : Number(ratingFilter),
        hidden: hiddenFilter === "all" ? undefined : hiddenFilter,
        limit,
        offset: page * limit,
      }),
    enabled: !!venueId,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["reviews", venueId] });

  const hideMutation = useMutation({ mutationFn: hideReview, onSuccess: invalidate });
  const unhideMutation = useMutation({ mutationFn: unhideReview, onSuccess: invalidate });
  const deleteMutation = useMutation({ mutationFn: deleteReview, onSuccess: invalidate });

  const reviews = data?.reviews ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <Star className="size-6" />
          {t("reviews")}
        </h1>
        <span className="text-sm text-muted-foreground">{total}</span>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-2">
        <Select value={ratingFilter} onValueChange={(v) => { setRatingFilter(v ?? "all"); setPage(0); }}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("review_filter_all_ratings")}</SelectItem>
            <SelectItem value="5">★★★★★</SelectItem>
            <SelectItem value="4">★★★★☆</SelectItem>
            <SelectItem value="3">★★★☆☆</SelectItem>
            <SelectItem value="2">★★☆☆☆</SelectItem>
            <SelectItem value="1">★☆☆☆☆</SelectItem>
          </SelectContent>
        </Select>

        <Select value={hiddenFilter} onValueChange={(v) => { setHiddenFilter((v as typeof hiddenFilter) ?? "all"); setPage(0); }}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("review_filter_all")}</SelectItem>
            <SelectItem value="false">{t("review_filter_visible")}</SelectItem>
            <SelectItem value="true">{t("review_filter_hidden")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <p className="py-8 text-center text-muted-foreground">{t("loading")}</p>
      ) : reviews.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16">
          <Star className="mb-4 size-12 text-muted-foreground" />
          <p className="text-muted-foreground">{t("no_reviews")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <Card key={r.id} className={r.isHidden ? "opacity-60" : ""}>
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <StarRow rating={r.rating} />
                      <span className="font-semibold">{r.itemName}</span>
                      {r.isHidden && (
                        <Badge variant="outline" className="text-destructive">
                          {t("review_hidden_badge")}
                        </Badge>
                      )}
                    </div>
                    {r.comment && (
                      <p className="mt-1 text-sm text-foreground/80">“{r.comment}”</p>
                    )}
                    <p className="mt-2 text-xs text-muted-foreground">
                      {r.customer.name || r.customer.email} ·{" "}
                      {new Date(r.createdAt).toLocaleDateString("tr-TR")}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    {r.isHidden ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        title={t("unhide_review")}
                        onClick={() => unhideMutation.mutate(r.id)}
                      >
                        <Eye className="size-4" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        title={t("hide_review")}
                        onClick={() => hideMutation.mutate(r.id)}
                      >
                        <EyeOff className="size-4" />
                      </Button>
                    )}
                    <ConfirmDialog
                      trigger={
                        <Button variant="ghost" size="icon" className="size-8">
                          <Trash2 className="size-4 text-destructive" />
                        </Button>
                      }
                      title={t("delete_review")}
                      description={t("delete_review_confirm")}
                      confirmLabel={t("delete")}
                      onConfirm={() => deleteMutation.mutate(r.id)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
          <span>{reviews.length} / {total}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
              {t("back")}
            </Button>
            <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
              {t("next")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
